import AppContext from 'App/AppContext';
import { IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import PeriodFilter, { IPeriodFilterData, PeriodFilterDataOption } from 'components/Filter/PeriodFilter';
import { getPeriodFilterEndDate, getPeriodFilterStartDate } from 'components/Filter/PeriodFilterHelper';
import KPIDataContextFilter from 'components/KPI/Data/KPIDataContextFilter';
import KPIDataView from 'components/KPI/Data/KPIDataView';
import { getAggregationIntervalOptions, getAggregationMethodOptions } from 'components/KPI/KPIHelpers';
import EntityPicker from 'components/Pickers/EntityPicker';
import { globalStackTokensGapSmall } from 'globalStyles';
import { mapToKPIContextFilter } from 'mappings/kpiMapping';
import { DashboardTypes } from 'models/dashboard';
import { KPIGraphDataConfigDTO } from 'models/dto/kpi/kpiGraphDataDTO';
import Entity, { EntityTypes } from 'models/entity';
import KPI, { KPIAggregationIntervals, KPIAggregationMethods, KPIChartTypes } from 'models/kpi/kpi';
import { KPIContextFilter } from 'models/kpi/kpiDataContext';
import { FormEvent, Fragment, FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataSingleKPI } from 'services/Api/dashboardService';
import { apiGetKPI, apiGetKPIs } from 'services/Api/kpiService';
import { apiRequest } from 'services/Auth/authConfig';
import { fromApiDateOptional, toApiDateTimeOptional } from 'utils/datetime';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetSingleKPIConfig {
  kpiId: number;

  periodStart: string | undefined; //deprecated - will be replaced by period

  periodEnd: string | undefined; //deprecated - will be replaced by period

  period?: IPeriodFilterData;

  aggregationMethod: KPIAggregationMethods;

  aggregationInterval: KPIAggregationIntervals;

  rawFilter: string;

  filter: KPIContextFilter;

  constructor() {
    this.kpiId = 0;
    this.periodStart = undefined;
    this.periodEnd = undefined;
    this.aggregationMethod = KPIAggregationMethods.Count;
    this.aggregationInterval = KPIAggregationIntervals.Month;
    this.rawFilter = '';
    this.filter = new KPIContextFilter();
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.kpiId = newRawConfig.kpiId ?? 0;
        this.aggregationMethod = newRawConfig.aggregationMethod ?? KPIAggregationMethods.Count;
        this.aggregationInterval = newRawConfig.aggregationInterval ?? KPIAggregationIntervals.Month;
        this.periodStart = newRawConfig.periodStart;
        this.periodEnd = newRawConfig.periodEnd;
        this.rawFilter = newRawConfig.rawFilter;
        if (this.rawFilter) {
          this.filter = KPIContextFilter.fromJSON(this.rawFilter);
        }
        this.period = newRawConfig.period;
        //backward compatibility - if period is not defined, try to create it from periodStart and periodEnd
        if (!this.period) {
          this.period = {
            option: PeriodFilterDataOption.Custom,
            customStart: this.periodStart,
            customEnd: this.periodEnd,
          };
        }
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetSingleKPIConfig {
    const newConfig = new WidgetSingleKPIConfig();
    newConfig.kpiId = this.kpiId;
    newConfig.aggregationMethod = this.aggregationMethod;
    newConfig.aggregationInterval = this.aggregationInterval;
    newConfig.periodStart = this.periodStart;
    newConfig.periodEnd = this.periodEnd;
    newConfig.filter = this.filter;
    newConfig.rawFilter = this.rawFilter;
    newConfig.period = this.period;

    return newConfig;
  }
}

interface IWidgetSingleKPIProps extends IWidgetRendererProps {}

const WidgetSingleKPI: FunctionComponent<IWidgetSingleKPIProps> = (props: IWidgetSingleKPIProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [kpi, setKPI] = useState<KPI | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [config, setConfig] = useState<WidgetSingleKPIConfig | undefined>(undefined);

  //
  // Effects
  //
  useEffect(() => {
    const loadConfig = (): WidgetSingleKPIConfig => {
      const newConfig = new WidgetSingleKPIConfig();
      newConfig.load(props.widget.widgetConfig);

      if (props.period) {
        newConfig.period = props.period; //override period with the one from props
      }

      return newConfig;
    };

    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);
        setKPI(undefined);
        setConfig(undefined);

        const config = loadConfig();
        setConfig(config);

        if (config && config.kpiId) {
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          const kpi = await apiGetKPI(config.kpiId, accessToken, undefined);

          const graphConfig = mapToKPIGraphDataConfigFromSingleKPIWidgetConfig(config);
          const data = await apiGetWidgetDataSingleKPI(graphConfig, accessToken, props.abortSignal);

          if (kpi) kpi.graphData = data;
          setKPI(kpi);
        }
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  //
  // Helpers
  //
  const mapToKPIGraphDataConfigFromSingleKPIWidgetConfig = (config: WidgetSingleKPIConfig): KPIGraphDataConfigDTO => {
    const output = new KPIGraphDataConfigDTO();

    //calculate start and end dates
    const customStart = fromApiDateOptional(config.period?.customStart);
    const customEnd = fromApiDateOptional(config.period?.customEnd);
    const start = getPeriodFilterStartDate(config.period?.option, config.period?.auditYear, customStart, appContext);
    const end = getPeriodFilterEndDate(config.period?.option, config.period?.auditYear, customEnd, appContext);

    output.kpiId = config.kpiId;
    output.aggregationInterval = config.aggregationInterval;
    output.aggregationMethod = config.aggregationMethod;
    output.periodStart = start ? toApiDateTimeOptional(start) : config.periodStart; //fallback to old periodStart if period is not defined
    output.periodEnd = end ? toApiDateTimeOptional(end) : config.periodEnd; //fallback to old periodEnd if period is not defined
    output.contextFilter = mapToKPIContextFilter(config.filter);
    output.chartType = KPIChartTypes.Bar;

    return output;
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!kpi) {
    //config is invalid
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:SingleKPI.Config.Invalid')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <KPIDataView
        isLoading={isLoading}
        kpi={kpi}
        chartType={KPIChartTypes.Bar}
        aggregationMethod={config?.aggregationMethod}
      />
    </IPStack>
  );
};

export default WidgetSingleKPI;

//
// Config
//

interface IWidgetConfigSingleKPIProps extends IWidgetConfigRendererProps {}

export const WidgetConfigSingleKPI = (props: IWidgetConfigSingleKPIProps) => {
  const { t } = useTranslation(['translation', 'widgets', 'kpis', 'dateTimeComponent', 'tasks']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [kpis, setKPIs] = useState<KPI[]>([]);
  const [kpiEntities, setKPIEntities] = useState<Entity[]>([]);
  const [selectedKPI, setSelectedKPI] = useState<KPI | undefined>(undefined);
  const [selectedEntity, setSelectedEntity] = useState<Entity | undefined>(undefined);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  const loadConfig = (): WidgetSingleKPIConfig => {
    const newConfig = new WidgetSingleKPIConfig();
    newConfig.load(props.widget.widgetConfig);

    if (props.period) {
      newConfig.period = props.period; //override period with the one from props
    }

    return newConfig;
  };

  const [config, setConfig] = useState<WidgetSingleKPIConfig>(loadConfig());

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadData = async () => {
    try {
      if (isLoading || currentWidgetId === props.widget.widgetId) return;
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      let kpis = await apiGetKPIs(accessToken, undefined);
      kpis = kpis.filter((k) => k.isCalcType());
      setKPIs(kpis);

      const entities = getEntitiesFromKPIs(kpis);
      setKPIEntities(entities);

      if (config.kpiId) {
        const entity = entities.find((e) => e.entityId === config.kpiId);
        setSelectedEntity(entity);
        setSelectedKPI(kpis.find((k) => k.kpiId === config.kpiId));
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getEntitiesFromKPIs = (kpis: KPI[]): Entity[] => {
    const entities: Entity[] = [];

    entities.push(
      ...kpis.map((c) => {
        const newEntity = new Entity();
        newEntity.entityId = c.kpiId;
        newEntity.typeOfEntity = EntityTypes.KPI;
        newEntity.entityName = c.name;

        return newEntity;
      }),
    );

    return entities;
  };

  const isConfigValid = (config: WidgetSingleKPIConfig): boolean => {
    //kpi must be selected
    if (config.kpiId <= 0) return false;

    return true;
  };

  const updateConfig = (config: WidgetSingleKPIConfig) => {
    const newConfig = config.clone();
    newConfig.rawFilter = JSON.stringify(newConfig.filter);
    setConfig(newConfig);
    props.onUpdateConfig(JSON.stringify(newConfig), isConfigValid(newConfig));
  };

  const onAddSelectedEntity = (entity: Entity) => {
    setSelectedEntity(entity);
    config.kpiId = entity.entityId;
    updateConfig(config);
    setSelectedKPI(kpis.find((k) => k.kpiId === entity.entityId));
  };

  const onRemoveSelectedEntity = () => {
    setSelectedEntity(undefined);
    config.kpiId = 0;
    updateConfig(config);
  };

  const onSetPeriod = (period: IPeriodFilterData | undefined) => {
    config.periodStart = undefined; //clear old periodStart and periodEnd to migrate to new period field
    config.periodEnd = undefined;
    config.period = period;
    updateConfig(config);
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPLabel>{t('widgets:SingleKPI.Config.KPI.Label')}</IPLabel>
        <EntityPicker
          entities={kpiEntities}
          isLoading={isLoading}
          addSelectedEntity={onAddSelectedEntity}
          clearSearchText={onRemoveSelectedEntity}
          loadData={loadData}
          setSearchTextToSelectedEntity={true}
          selectedEntity={selectedEntity}
          isOnPanel={true}
        ></EntityPicker>
      </IPStack.Item>
      {selectedEntity && selectedKPI && (
        <Fragment>
          <IPStack.Item>
            <IPDropdown
              label={t('widgets:SingleKPI.Config.AggregationMethod.Label')}
              selectedKey={config.aggregationMethod}
              options={getAggregationMethodOptions(t)}
              styles={{ listbox: { maxHeight: 250 } }}
              onChange={(event: FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
                config.aggregationMethod = option?.key as KPIAggregationMethods;
                updateConfig(config);
              }}
            />
          </IPStack.Item>
          <IPStack.Item>
            <IPDropdown
              label={t('widgets:SingleKPI.Config.AggregationInterval.Label')}
              selectedKey={config.aggregationInterval}
              options={getAggregationIntervalOptions(t)}
              styles={{ listbox: { maxHeight: 250 } }}
              onChange={(event: FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
                config.aggregationInterval = option?.key as KPIAggregationIntervals;
                updateConfig(config);
              }}
            />
          </IPStack.Item>
          <IPStack.Item>
            <IPStack.Item>
              <PeriodFilter
                showOption={true}
                showCurrentAuditYear={true}
                storageKey={undefined}
                onUpdatePeriodOption={onSetPeriod}
                period={config.period}
                showAll={true}
                disabled={
                  props.widget.dashboard?.dashboardType === DashboardTypes.control &&
                  props.widget.dashboard?.isContextType()
                }
              />
            </IPStack.Item>
          </IPStack.Item>
          <IPStack.Item>
            <KPIDataContextFilter
              kpi={selectedKPI}
              filter={config.filter}
              onUpdate={(newFilter) => {
                const newConfig = config.clone();
                newConfig.filter = newFilter;
                updateConfig(newConfig);
              }}
            />
          </IPStack.Item>
        </Fragment>
      )}
    </IPStack>
  );
};
